/**
 * Задача, написанная на витрине, — по дороге в кабинет.
 *
 * Витрина и кабинет — разные бандлы с разными точками входа (`site.html` и
 * `app.html`), и общей памяти у них нет никакой: переход между ними это полная
 * перезагрузка страницы. Поэтому написанное на главной надо где-то положить,
 * а после входа поднять.
 *
 * Здесь лежит НЕ текст, а короткий токен: сам текст уехал на сервер сразу и
 * живёт там сутки. Так задача переживает то, чего браузерная память не
 * переживает, — письмо, открытое в другом браузере, продолжение с телефона,
 * очистку вкладки. И так в хранилище браузера не остаётся чужого делового
 * текста после того, как человек уже вошёл.
 *
 * localStorage, а не cookie: токен нужен только браузеру и уходит на сервер
 * один раз — когда кабинет забирает задачу. Cookie каталась бы с каждым
 * запросом впустую. Модуль — калька `analytics/attribution.ts`: тот же приём с
 * TTL, тем же молчаливым поведением при запрещённом хранилище.
 */
const KEY = 'zetter.draft';

/**
 * Сколько токен считается годным.
 *
 * Ровно столько же, сколько черновик живёт на сервере. Держать дольше значило
 * бы подставлять в кабинете задачу, которой там уже нет, и объяснять человеку
 * ошибку вместо результата.
 */
const TTL_MS = 24 * 60 * 60 * 1000;

interface Stored {
  token: string;
  /** Жанр, выбранный чипом: подставляется вместе с текстом. */
  kind: string;
  /** Метка времени записи: по ней запись протухает. */
  savedAt: number;
}

/** Запомнить токен задачи, принятой сервером. */
export function saveDraftToken(input: { token: string; kind: string }): void {
  try {
    const value: Stored = { token: input.token, kind: input.kind, savedAt: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /*
     * Приватный режим и запрет хранилища.
     *
     * Молча: текст уже на сервере, и человек всё равно уходит регистрироваться.
     * Потеряется только автоподстановка — ради неё показывать ошибку на первом
     * экране нельзя.
     */
  }
}

/** Токен задачи, если он есть и не протух. */
export function readDraftToken(): { token: string; kind: string } | null {
  const stored = read();
  return stored ? { token: stored.token, kind: stored.kind } : null;
}

/**
 * Есть ли что подхватывать.
 *
 * Нужен онбордингу: он решает, куда вести после создания организации — в
 * каталог шаблонов или сразу к работе, где задача уже подставлена.
 */
export function hasDraft(): boolean {
  return read() !== null;
}

/**
 * Забыть токен.
 *
 * Зовётся после того, как проект по задаче заведён. Оставшись, токен подставил
 * бы ту же задачу в следующий пустой стартовый экран — человек начал бы второй
 * документ с текстом первого.
 */
export function clearDraft(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Нечего чистить, если хранилище запрещено: туда ничего и не легло.
  }
}

function read(): Stored | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Stored;
    if (typeof parsed?.token !== 'string' || !parsed.token) return null;
    if (typeof parsed?.savedAt !== 'number') return null;

    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
